// The console's HTTP client for /admin/*.
//
// Small on purpose, with one job the calling screens must not have to remember:
// TURN A FAILURE INTO SOMETHING TRUE AND SPECIFIC.
//
// ⚠️ The failure this exists to prevent, copied from a real one in apps/fe.
// `app.jsx:511` reads `if (!res.ok) return;` — a non-2xx response is discarded
// and the screen renders as though nothing happened. That single line is what
// would make a broken Auth0 Action, an unapplied migration or an expired token
// look like "the console is empty" rather than "the console is broken", and it
// cost real debugging time on the authoring side. Every non-2xx here becomes a
// typed error the screen is obliged to render.

import type {
  AiCostBucket,
  CourseImportRuleKey,
  CourseImportRuleSource,
  CourseImportRules,
} from "@dynamo/schema";
import type { SessionClient } from "./auth.js";

/**
 * What the console says when OUR server answered but could not get a pass from Auth0 (slow or
 * rate-limiting). One sentence for the screens' error box below AND the start screen (app.tsx), so
 * the two cannot drift. It deliberately does not say "check your connection": the operator's
 * network is fine in this case, and that advice sends them to the wrong remedy (Omar, 2026-10-18).
 */
export const SIGN_IN_SERVICE_DID_NOT_ANSWER = "The sign-in service didn’t answer. Try again.";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** True when the environment simply has not been provisioned yet. */
  readonly notProvisioned: boolean;
  /**
   * The gateway's own heading for this failure, when it sends one.
   *
   * Added by the gateway on 2026-08-21 because a single heading over every 403
   * made four different refusals read as one. If it is absent the screen falls
   * back to a generic heading — never to a guess about which refusal this is.
   */
  readonly title: string | undefined;

  constructor(status: number, code: string, message: string, title?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    // A 503 from our own sign-in service is the network or Auth0, not a missing setup:
    // headed "Not set up in this environment", it sent the reader to the wrong remedy
    // (QA of plan 27, F1).
    this.notProvisioned = status === 503 && code !== "auth_unavailable";
    this.title = title;
  }
}

/** Whether a layer is the factory text or something staff saved over it. */
export type ImageProfileSource = "factory" | "stored";

export interface SharedImageProfile {
  /** What a generation would use RIGHT NOW — stored text if there is any. */
  craft: string;
  guidance: string;
  sources: { craft: ImageProfileSource; guidance: ImageProfileSource };
  /** The protected original, sent alongside so "restore" needs no second call. */
  factory: { craft: string; guidance: string };
  updatedAt: string | null;
  updatedBySub: string | null;
}

export interface SavedSharedImageProfile {
  saved: boolean;
  craft: string;
  guidance: string;
  sources: { craft: ImageProfileSource; guidance: ImageProfileSource };
}

export interface CustomerIllustrationStyle {
  illustration: string;
  source: ImageProfileSource;
  factory: string;
  updatedAt: string | null;
  updatedBySub: string | null;
}

// ── THE COURSE-IMPORT RULES ─────────────────────────────────────────────────
//
// Omar, 2026-09-18: "I want to manage the rules for automatic course generation
// directly from the Console, so I can change how future imports work without
// code changes or deployment."
//
// ★ NOTHING IS RE-DECLARED HERE. `CourseImportRules`, its key union and its
// source union come from `@dynamo/schema`, which is also what the gateway
// resolves and what the generator obeys. A second declaration of the same seven
// fields in this file would compile happily while disagreeing with the server
// about a field NAME, and the mismatch would be invisible
// (`feedback_a_redeclared_type_makes_a_name_mismatch_silent`).

/** One choosable opening layout, as the ROUTE sends it. */
export interface CourseImportLayoutChoice {
  id: string;
  label: string;
  /**
   * Which composable slots this layout HAS. Sent by the gateway, which owns the
   * layout set — the Console never keeps a list of its own
   * (`feedback_one_rule_one_place`). Optional so an older gateway simply offers
   * fewer ticks rather than throwing.
   */
  slots?: { subtitle: boolean; labels: boolean };
}

export interface CourseImportSettings {
  /** What the NEXT import would obey — stored values merged over the factory. */
  rules: CourseImportRules;
  /** Per rule: is this the protected original, or something staff saved? */
  sources: Record<CourseImportRuleKey, CourseImportRuleSource>;
  /** The protected originals, sent alongside so "restore" needs no second call. */
  factory: CourseImportRules;
  /**
   * The layout ids a module may be allowed to open with.
   *
   * ★ SENT BY THE SERVER, never held here. The closed set belongs to the
   * generator; a copy in this app would be a second list that agrees only while
   * somebody remembers both (`feedback_one_rule_one_place`).
   */
  layoutChoices: CourseImportLayoutChoice[];
  /**
   * The exact text injected into the outline prompt, produced by the same
   * function the gateway calls. The screen renders it verbatim — a description
   * of it would be a second copy and would go stale silently.
   */
  promptBlock: string;
  /** What no setting can change, and why — rendered on its own tab. */
  invariants: { title: string; body: string }[];
  updatedAt: string | null;
  updatedBySub: string | null;
}

/**
 * The write body — the Photography three-way convention, per rule key.
 *
 * ⚠️ ABSENT, `null` AND A VALUE ARE THREE DIFFERENT THINGS, and the difference
 * is the whole of "restore the original": absent leaves the rule alone, `null`
 * REMOVES the override so the factory value applies again, and a value stores
 * it. Collapsing the first two into one optional field would make "restore"
 * unexpressible (`feedback_off_is_not_absent`).
 *
 * Derived from `CourseImportRules` by a mapped type rather than retyped, so a
 * rule added to the schema is writable here without an edit — and cannot be
 * given the wrong type here.
 */
export type CourseImportRuleWrites = {
  [K in CourseImportRuleKey]?: CourseImportRules[K] | null;
};

export interface SavedCourseImportRules {
  saved: boolean;
  rules: CourseImportRules;
  sources: Record<CourseImportRuleKey, CourseImportRuleSource>;
  /** Recomputed from what was STORED, so the screen can show the new text. */
  promptBlock: string;
}

export interface CustomerSummary {
  id: string;
  slug: string;
  displayName: string;
  orgId: string;
  status: string;
  isInternal: boolean;
  /**
   * True when this customer's exported packages ship with their course files
   * made unreadable. Changing it affects packages built afterwards only.
   */
  protectExports: boolean;
  contactName: string | null;
  contactEmail: string | null;
  auth0OrgId: string | null;
  createdAt: string;
  provisionedAt: string | null;
}

export interface CustomerList {
  customers: CustomerSummary[];
  total: number;
  /**
   * How many rows the internal/test filter is hiding.
   *
   * Reported rather than merely subtracted, because Omar's instruction was that
   * a test company "never pollutes your customer numbers" — and a list that
   * silently shows 3 of 4 is its own small lie. The screen says "1 internal
   * hidden" so the number on screen is always accountable.
   */
  internalHidden: number;
}

export interface CustomerDetail {
  customer: CustomerSummary;
  stats: {
    courseCount: number;
    userCount: number;
    lastActivity: string | null;
  };
  auth0: { organizationId: string | null; provisioned: boolean };
}

/**
 * The "AI costs" report for one customer, over one period.
 *
 * ★ EVERY FIGURE HERE IS AN ESTIMATE THIS SYSTEM COMPUTED from what it recorded,
 * and the field names say so. Omar, 2026-09-07: "Keep client-level estimates
 * clearly distinguishable from Google's billing figures, and explain any
 * differences without arbitrarily assigning them to clients." There is ONE
 * Google Cloud project behind every client, so Google's invoice cannot be split
 * per client by anything Google knows — only by what this ledger recorded.
 */
export interface AiCostTotals {
  requests: number;
  /** Paid calls, INCLUDING retries and discards. Always ≥ `requests`. */
  attempts: number;
  succeeded: number;
  /** Paid for and unusable — the discarded generations. */
  unusable: number;
  failed: number;
  promptTokens: number;
  outputTokens: number;
  totalTokens: number;
  /** Summed over the requests that HAVE a rate — never over the ones that do not. */
  estimatedCostUsd: number;
  /** How many requests contributed nothing to the figure above. */
  requestsWithoutCost: number;
}

/**
 * One measured quantity with the unit it is measured in.
 *
 * ★ A LIST, NOT A NUMBER, because a group can hold rows measured in different
 * things. Adding 1,284 images to 2,480,913 characters produces a confident
 * number that means nothing, and a screen cannot tell it apart from a real one.
 */
export interface AiCostUnitTotal {
  /** As recorded — `images`, `characters`, `audio_seconds`. Humanised on screen. */
  unit: string;
  amount: number;
}

export interface AiCostGroup extends AiCostTotals {
  key: string;
  label: string;
  /** Each unit with its own total. Empty when the group records no volume. */
  units: AiCostUnitTotal[];
  /** @deprecated A sum ACROSS units when there is more than one. Read `units`. */
  billableUnits: number;
  /** `"mixed"` when the group disagrees with itself. Read `units`. */
  billableUnit: string | null;
  /**
   * On a `byCourse` row: present ONLY when the client deleted this course
   * through the app's own delete door and the gateway recorded it (Omar,
   * 2026-09-19: "The badge must reflect a recorded client deletion"). The
   * row's `label` is then the course's ORIGINAL title. Absent both for live
   * courses and for rows deleted before the recording existed — those keep the
   * server's plain "Deleted course" label and must get NO badge.
   *
   * ⚠️ OPTIONAL because the gateway and this app deploy from one merge but not
   *    at one moment, and migration 0025 is applied by hand per environment.
   *    Absent means "no deletion recorded", NEVER "not deleted"
   *    (`feedback_two_hosts_one_merge_means_a_new_api_field_must_be_optional`).
   */
  deletedByClient?: {
    /** The title the course had when it was deleted; null when unrecorded. */
    title: string | null;
    /** When the client deleted it (ISO); null when the record has no time. */
    deletedAt: string | null;
  };
}

/** One course's figures for ONE category — a row of the drill-in view. */
export interface AiCourseCategoryGroup extends AiCostGroup {
  /** `__unattributed__` for work not tied to a course. Matches `byCourse`'s key. */
  courseId: string;
  bucket: AiCostBucket;
}

/**
 * One line of a PROVIDER's own report, as the provider stated it.
 *
 * ★ NOT the same kind of number as the fields on `AiCostTotals`, and the screen
 *   must never add the two together. Those are this system's own arithmetic over
 *   its usage ledger; this is what an invoice says. Omar, 2026-09-08: "Do not
 *   present a calculation based on usage multiplied by list prices as an actual
 *   provider charge."
 */
/**
 * Money, always carrying its currency — nothing on this screen converts.
 *
 * ── ★ THREE FIGURES (Omar, 2026-09-16) ──────────────────────────────────────
 *
 *   "I want to see the usage cost reported by Google BEFORE credits are
 *    applied. Promotional credits explain how the cost is covered; they must
 *    not hide the cost of using the service … Any credits and net payable
 *    amount should be clearly distinguished."
 *
 * ⚠️ `gross` and `credit` are OPTIONAL because the gateway and this app deploy
 *    from one merge but not at one moment, and because the gateway itself
 *    reports no credit until migration 0023 is applied — which is done BY HAND,
 *    per environment. Absent means "no credit information", NEVER "zero credit"
 *    (`feedback_two_hosts_one_merge_means_a_new_api_field_must_be_optional`).
 */
export interface ChargedAmount {
  currency: string;
  /** NET — what is actually payable. Unchanged meaning. */
  amount: number;
  /** The usage cost BEFORE credits. */
  gross?: number;
  /** Credits applied, negative. Absent when no line carried one. */
  credit?: number;
}

export interface AiProviderCostRow {
  provider: string;
  /**
   * Which of the five categories this provider's charge belongs to, or `null`
   * when it cannot be placed from what the provider reported.
   *
   * ★ `null` is a real answer, not a gap — and since 2026-09-11 it no longer
   *   means "Google". Google's charges are now placed from `service` and
   *   `projectId` together. What still comes back `null` is Dynamo's OWN
   *   infrastructure (BigQuery, which bills to the same project as translation
   *   and must not be counted as translation), a service no rule recognises
   *   yet, and any provider we do not know. This screen shows those at provider
   *   level rather than guessing.
   */
  category: string | null;
  /**
   * Google's own words for what was billed. `null`/absent for every other
   * provider, which reports one category and needs no discriminator.
   *
   * ⚠️ OPTIONAL because migration 0019 is what makes the gateway return them,
   * and a Console deployed against an older gateway must render rather than
   * crash (`feedback_two_hosts_one_merge_means_a_new_api_field_must_be_optional`).
   */
  service?: string | null;
  projectId?: string | null;
  /** The provider's own line item, verbatim. */
  lineItem: string | null;
  /** ISO 4217, as reported. NEVER converted — see the note in the section that renders it. */
  currency: string;
  /** NET — what is payable, after credits. Unchanged meaning. */
  amount: number;
  /**
   * The usage cost BEFORE credits, and the credits themselves.
   *
   * ⚠️ BOTH OPTIONAL, and `creditAmount: null` is a THIRD state: the provider
   *    reported this line and applied no credit to it. Absent means the gateway
   *    could not tell us (no migration 0023 yet); null means it told us there
   *    was none (`feedback_absence_and_emptiness_read_the_same`).
   */
  grossAmount?: number;
  creditAmount?: number | null;
  periodStart: string;
  periodEnd: string;
  /** Traceability, e.g. `deepgram:billing/breakdown`. */
  source: string;
  importedAt: string;
}

/**
 * What one provider's cost import is DOING, in one word.
 *
 * ★ The field this whole status route exists for. Omar, 2026-09-10: an import
 *   that cannot run because access is "missing, revoked or expired" must
 *   "produce a clear connection error, rather than remain indefinitely labelled
 *   'Awaiting provider cost'". `failing` is that error.
 */
export type CostImportHealth =
  | "not_configured"
  | "never_run"
  | "running"
  | "healthy"
  | "stale"
  | "failing";

export interface CostImportProviderStatus {
  provider: string;
  configured: boolean;
  health: CostImportHealth;
  lastSuccessAt: string | null;
  lastSuccessWindowEnd: string | null;
  lastAttemptAt: string | null;
  lastAttemptOutcome: string | null;
  /** The provider's own words where there are any — for a 403, the missing scope. */
  lastAttemptMessage: string | null;
}

export interface CostImportStatus {
  providers: CostImportProviderStatus[];
  configured: Record<string, boolean>;
  schedule: {
    /** False when the automatic import is switched off ON PURPOSE. */
    automatic: boolean;
    intervalMinutes: number;
    lookbackDays: number;
    staleAfterHours: number;
  };
}

/**
 * Why a charge carries no client — Google only.
 *
 * ★ THREE DIFFERENT THINGS ARRIVE IN THE GAP LIST AND ONLY ONE IS A FAULT.
 *   `infrastructure` is Dynamo's own running cost and will never have a client;
 *   `unknown` is a service no rule recognises yet; `workload` with no marker is
 *   the genuine attribution gap. Showing all three identically would send
 *   somebody looking for an attribution that does not exist
 *   (`feedback_absence_and_emptiness_read_the_same`).
 */
export interface AiCostClassification {
  kind: "workload" | "infrastructure" | "unknown";
  workload?: "translation" | "images";
  /** Plain-language, written for a reader who did not build this. */
  reason?: string;
  /** The rule that matched has never yet matched a real charge in this account. */
  provisional: boolean;
}

export interface AiAttributionGap {
  provider: string;
  /** The tag exactly as the provider reported it, unparsed. */
  attributionKey: string | null;
  lineItem: string | null;
  currency: string;
  amount: number;
  source: string;
  importedAt: string;
  /** ⚠️ OPTIONAL — see `AiProviderCostRow.service`. Absent on an older gateway. */
  classification?: AiCostClassification | null;
  service?: string | null;
  projectId?: string | null;
}

export interface AiAttributionGaps {
  window: { start: string; end: string };
  gaps: AiAttributionGap[];
  totalsByCurrency: Array<{ currency: string; amount: number }>;
}

// ⚠️ `ServiceCostComparison` and `CostColumns` were removed here — Omar,
// 2026-09-15: "Completely remove the Cost comparison area ... and any piece of
// code related to that section." They described the OQ-113 panel and nothing
// else. The gateway no longer sends `costColumns`; `ai-cost-columns.ts` and
// `ai-cost-reconcile.ts` are deleted. Dynamo's own per-operation calculation is
// still RECORDED in `ai_usage` and Google's own amounts in `ai_provider_cost` —
// only the side-by-side comparison went.

/**
 * A workspace's people, as the Console sees them (migration 0021).
 *
 * ★ `contactEmail` and `contactHasAccount` travel TOGETHER on purpose. A
 *   contact with no member row is an ordinary state — a workspace registered
 *   before anyone signed in — and without the second field the screen could not
 *   tell it from "this customer has no owner", which is a different claim
 *   (`feedback_absence_and_emptiness_read_the_same`).
 */
export interface WorkspaceMember {
  email: string;
  displayName: string | null;
  role: string;
  status: string;
  assignedLangs: string[];
  invitedAt: string | null;
  activatedAt: string | null;
  createdAt: string;
  /** This member is the address the workspace was registered for. */
  isContact: boolean;
}

export interface WorkspaceMembers {
  contactEmail: string | null;
  contactHasAccount: boolean;
  members: WorkspaceMember[];
}

export interface AiCostReport {
  period: { from: string; to: string };
  /**
   * The two approved columns, images and translation kept separate.
   *
   * ★ OPTIONAL, DELIBERATELY. The Console (Cloudflare Pages) and the gateway
   *   (Railway) deploy from the same merge but not at the same moment, so for
   *   those minutes this screen is the new one talking to the old API and the
   *   field is genuinely absent. Required, it would blank the whole tab
   *   (`feedback_two_hosts_one_merge_means_a_new_api_field_must_be_optional`).
   */
  /** Whether this workspace is one of ours — read fresh from the registry. */
  isInternal: boolean;
  totals: AiCostTotals;
  byOperation: AiCostGroup[];
  byCourse: AiCostGroup[];
  /**
   * ★ Every course crossed with every category, in THIS payload.
   *
   * Opening a course must not fetch again. A second request — same route, same
   * window — can come back with a different total, and the drill-in would then
   * disagree with the row it was opened from, with no way to tell which was
   * right. One payload, one period, one set of numbers.
   */
  byCourseOperation: AiCourseCategoryGroup[];
  byEnvironment: AiCostGroup[];
  models: string[];
  lastRecordedAt: string | null;
  /**
   * What the PROVIDERS say this client cost, for the same window.
   *
   * ★ Beside this system's own figures, never merged into them. Empty means no
   *   provider has reported anything for this client yet — which is NOT the same
   *   as nothing having been spent, and the screen must say which.
   */
  providerCost: AiProviderCostRow[];
  /**
   * Per category: how far the providers have reported, and whether anything has
   * been recorded since.
   *
   * ── Why (Omar, 2026-09-16) ────────────────────────────────────────────────
   *
   *   "Until Google publishes the new billing data, keep that operation
   *    pending; an existing zero balance does not confirm it has been reported."
   *
   * ★ Without this, a category holding ANY figure made a brand-new operation
   *   look already billed — and every Google image figure on this account is a
   *   fully-credited 0.00, so "a charge exists" was permanently true and
   *   permanently uninformative.
   *
   * ⚠️ OPTIONAL — absent on a gateway that predates it, and the screen must then
   *    fall back to what it can say rather than claiming nothing is pending.
   */
  chargeCoverage?: Array<{
    key: string;
    /** Exclusive, `YYYY-MM-DD`. Null when nothing has been reported at all. */
    reportedThrough: string | null;
    pending: boolean;
  }>;
  /**
   * ★ What each CATEGORY cost, and each COURSE's share of it.
   *
   * The category figures are the providers' own: one provider serves exactly one
   * of the five today, so its charge IS that category's charge. The per-course
   * figures are a SHARE — no provider reports per course and none can — divided
   * by what each course measurably used. The two are named differently on
   * purpose and this screen never calls a share a charge.
   */
  /**
   * ⚠️ OPTIONAL on purpose: the Console and the gateway deploy from the same
   * merge but not at the same moment, so for a few minutes the new screen can be
   * talking to an API that does not send this yet. Marked optional so the
   * compiler forces every reader to handle its absence rather than discovering
   * it as a blank panel in production.
   */
  courseCharges?: {
    byCategory: Array<{ key: string; charged: ChargedAmount[] }>;
    byCourse: Array<{
      courseId: string;
      key: string;
      share: ChargedAmount[];
      basis: "audio_seconds" | "tokens" | "requests" | "none";
    }>;
    /** Real money that no course in this window can account for. */
    unattributedShare: ChargedAmount[];
  };
  /**
   * Whether this company has its own OpenAI project — the thing that makes its
   * OpenAI spend separable at all.
   *
   * ★ Present so an empty OpenAI figure can be EXPLAINED rather than merely
   *   shown. A company that never finished setup and a company that has not
   *   generated any questions both display nothing, and only one of them needs
   *   somebody to act (`feedback_absence_and_emptiness_read_the_same`).
   */
  openAiSetup: {
    /** `ready` · `provisioning` · `incomplete` · `missing` (never set up). */
    state: string;
    /** The recorded remedy, in words, when setup stopped part-way. */
    detail: string | null;
    /** What OpenAI's own cost report groups by, so a figure can be checked by hand. */
    projectId: string | null;
  };
  google: {
    /** ★ ONE project, shared by every client. Never presented as this client's own. */
    sharedProjectId: string | null;
    billingUrl: string | null;
  };
}

export interface StaffIdentity {
  subject: string;
  email: string;
  roles: string[];
  consoleDbConfigured: boolean;
}

/** One row of the approval queue, as `/admin/requests` returns it. */
export interface RegistrationRequest {
  id: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  status: string;
  submittedAt: string;
  decidedAt: string | null;
  decisionReason: string | null;
  provisionedOrgId: string | null;
  /**
   * Computed per request by the gateway, never stored.
   *
   * ⚠️ A flag NEVER hides a row — decision D10/D11. `personal email` and
   * `domain already has a customer` are reasons to look closer, not reasons the
   * form was wrong, and a queue that filtered them would lose a real prospect
   * silently. The screen tints; Omar decides.
   */
  flags: string[];
}

export interface RequestList {
  requests: RegistrationRequest[];
  total: number;
  pending: number;
}

export interface ApproveResult {
  approved: true;
  orgId: string;
  clientId: string;
  auth0UserId: string;
  /** False when an EXISTING Auth0 identity was adopted rather than created. */
  auth0UserCreated: boolean;
  /** True when this request was already approved and nothing ran a second time. */
  alreadyApproved: boolean;
}

export interface RejectResult {
  rejected: true;
  spam: boolean;
}

export interface StatusChangeResult {
  changed: boolean;
  status: string;
  /** True when the customer was already in that state — reported, not hidden. */
  alreadyInThatState: boolean;
}

export interface ProtectionChangeResult {
  changed: boolean;
  protectExports: boolean;
  /** True when it was already in that state — reported, not hidden. */
  alreadyInThatState: boolean;
}

export interface ApproveInput {
  slug: string;
  displayName: string;
  reason: string;
  isInternal: boolean;
}

/**
 * One recorded import run, as the reader returns it.
 *
 * ⚠️ `findings` is COUNTED FROM THE ROWS by the database function, not read out
 * of `summary` — a tally that reads its own cached copy cannot notice when the
 * copy is wrong (`feedback_a_check_that_reads_its_own_output_is_circular`).
 */
export interface ImportRunRow {
  runId: string;
  orgId: string;
  courseId: string;
  environment: string;
  startedAt: string;
  finishedAt: string | null;
  rulesHash: string;
  rulesSource: string;
  /**
   * ★ THE RULES SNAPSHOT ITSELF (migration 0027), not only its fingerprint.
   *
   * It has been written on every run row since 0026 and was returned by
   * NOTHING — the Console showed a hash and nobody could open the rules behind
   * it. A field nobody can read is not recorded.
   */
  rules: unknown;
  promptVersion: string | null;
  findings: number;
  /**
   * The same total, split by which course it describes.
   *
   * ⚠️ "2 findings" can be two observations about the PLAN while the saved
   * course was never examined at all. Those are different facts and the screen
   * must be able to tell them apart.
   */
  planFindings: number;
  finalFindings: number;
  /** How many of the four evidence artefacts this run kept. Often 0. */
  artifacts: number;
  /** ① What the pipeline INTENDED. Null members mean the run predates 0027. */
  plan: {
    chapters: number | null;
    modules: number | null;
    screens: number | null;
  };
  /**
   * ② ★ WHAT THE AUTHOR ACTUALLY GOT — or `null`, meaning NOBODY MEASURED.
   *
   * ⚠️ `null` is NOT a measurement of zero, and a screen that renders it as one
   * re-creates the exact defect this field exists to close: on 2026-09-20 a run
   * recorded 41 screens and the saved course held 70. Both numbers were real;
   * nothing compared them (`feedback_absence_and_emptiness_read_the_same`).
   */
  final: ImportFinalMeasurement | null;
  /** `savedScreens - planScreens`, computed in SQL. Null when either is absent. */
  screensAddedAfterPlan: number | null;
  /** ③ What went in — the source file's identity and its exclusions. */
  source: {
    name: string | null;
    sha256: string | null;
    blocks: number | null;
    chars: number | null;
    truncated: boolean | null;
    tocSkipped: number | null;
  };
  plannerInput: {
    chars: number | null;
    truncated: boolean | null;
  };
  summary: {
    modules?: number;
    screens?: number;
    perClassification?: Record<string, number>;
    perStage?: Record<string, number>;
    perRule?: { ruleId: string; checked: number; violated: number }[];
  } | null;
}

/**
 * The run's result-side measurement — the SAVED course.
 *
 * ★ `retentionControl` TRAVELS WITH `retentionFraction`, always. The same
 * course measured against unrelated text must score near zero; without that
 * beside it, a matcher that had stopped discriminating would report a perfect
 * result and nothing would disagree
 * (`feedback_a_probe_without_a_control_cannot_fail_honestly`).
 */
export interface ImportFinalMeasurement {
  recordedAt: string;
  savedChapters: number | null;
  savedModules: number | null;
  savedScreens: number | null;
  /** ★ "It did not run" is its own outcome, and never a fraction of zero. */
  retentionRan: boolean;
  retentionReason: string | null;
  retentionFraction: number | null;
  retentionTarget: number | null;
  retentionControl: number | null;
  blocksTotal: number | null;
  blocksAbsent: number | null;
  recoveredPlacements: number | null;
  unresolvedPlacements: number | null;
}

/**
 * `GET /admin/course-import/runs/:runId/final`.
 *
 * ⚠️ `measured` IS THE ANSWER, not an implementation detail. A run with no
 * final row comes back `measured: false` carrying a sentence, so the screen
 * can say "not measured" in those words instead of printing zeros.
 */
export interface ImportFinalResponse {
  runId: string;
  measured: boolean;
  courseId?: string;
  final:
    | (ImportFinalMeasurement & { blocksExcluded: number | null; summary: unknown })
    | null;
  notMeasured: string | null;
}

/**
 * One kept artefact — WITHOUT its content, which the listing deliberately omits.
 *
 * ⚠️ `bytes` IS THE WHOLE ARTEFACT and the stored copy may hold less of it.
 * `truncated` says which, and the screen prints it beside the size: a stored
 * fragment reporting itself as complete would be worse than storing nothing.
 */
export interface ImportArtifactRow {
  kind: string;
  mediaType: string;
  bytes: number;
  sha256: string;
  truncated: boolean;
  recordedAt: string | null;
}

export interface ImportArtifactsResponse {
  runId: string;
  artifacts: ImportArtifactRow[];
  /** The closed set of four, sent by the server so no second copy exists. */
  kinds: string[];
  /** ★ What was NOT kept. The screen owes a reason for each of these. */
  missing: string[];
}

/** One finding. Every column the table holds — see migration 0026's header. */
export interface ImportFindingRow {
  runId: string;
  recordedAt: string;
  environment: string;
  /**
   * ★ 'plan' | 'final' (migration 0027) — which course this describes.
   *
   * Never inferred from anything else. A finding measured on the outline and
   * one measured on the saved draft are answers to different questions, and
   * rendering them in one undifferentiated list is how the gap between them
   * stayed invisible (`feedback_a_coverage_number_can_measure_the_plan_not_the_result`).
   */
  phase: string;
  courseId: string;
  chapterTitle: string;
  moduleTitle: string;
  moduleIndex: number;
  screenOrdinal: number | null;
  screenTitle: string;
  screenType: string;
  ruleId: string;
  ruleValue: unknown;
  expected: string;
  produced: string;
  originStage: string;
  retypedByStage: string | null;
  blockWords: number | null;
  blockChars: number | null;
  layoutCapacityChars: number | null;
  bandWordsP25: number | null;
  bandWordsP75: number | null;
  classification: string;
  classificationReason: string;
}

export class ConsoleApi {
  constructor(
    private readonly base: string,
    /** The console's half of the sign-in service: the 1-hour pass, in memory (plan 27). */
    private readonly session: SessionClient,
  ) {}

  private get<T>(path: string): Promise<T> {
    return this.request<T>("GET", path);
  }

  /**
   * A GET whose body is NOT JSON — the findings CSV.
   *
   * ⚠️ It shares `request`'s auth and error handling deliberately, and differs
   * in exactly one thing: it returns the text. Writing a second fetch here
   * would be a second place for the token, the base URL and the error envelope
   * to drift (`feedback_one_rule_one_place`).
   */
  private async getText(path: string): Promise<string> {
    return this.request<string>("GET", path, undefined, "text");
  }

  /**
   * ⚠️ Every caller of this is a button that changes something in the world —
   * one of them emails a real person. It is deliberately NOT retried here: a
   * transparent retry on a timeout is how one click becomes two invitations.
   * Retrying is the operator's decision, taken on a screen that tells them what
   * already happened.
   */
  private post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>("POST", path, body);
  }

  /**
   * The image profile's writer. Same no-retry rule as `post` — a transparent
   * retry on a timeout would save twice, and the second save would overwrite an
   * edit a colleague made in between.
   */
  private put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>("PUT", path, body);
  }

  private async request<T>(
    method: "GET" | "POST" | "PUT",
    path: string,
    body?: unknown,
    as: "json" | "text" = "json",
  ): Promise<T> {
    let token: string;
    try {
      token = await this.session.getPass();
    } catch (err) {
      // An ENDED session is a real, actionable state — sign in again. Our sign-in
      // service not answering is not: it is a network blip, and saying "your
      // session has expired" would send the operator to the wrong remedy.
      const code = (err as { error?: string }).error;
      if (code === "session_ended" || code === "signed_out") {
        throw new ApiError(401, "session_expired", "Your session has expired.");
      }
      throw new ApiError(
        503,
        "auth_unavailable",
        SIGN_IN_SERVICE_DID_NOT_ANSWER,
        // The heading the console's own start screen gives the same condition (app.tsx).
        "Sign-in unavailable",
      );
    }

    let res: Response;
    try {
      res = await fetch(`${this.base}${path}`, {
        method,
        headers: {
          authorization: `Bearer ${token}`,
          ...(body === undefined ? {} : { "content-type": "application/json" }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch (err) {
      throw new ApiError(
        0,
        "network",
        `Could not reach the gateway at ${this.base}. ` +
          (err instanceof Error ? err.message : "Network error."),
      );
    }

    if (!res.ok) {
      // Read the API's own envelope { code, message } when there is one. A
      // response body that is not JSON is itself informative — it usually means
      // a proxy or the SPA fallback answered instead of the gateway — so say so
      // rather than reporting a parse error.
      let code = `http_${res.status}`;
      let message = `${res.status} ${res.statusText}`;
      let title: string | undefined;
      const raw = await res.text();
      try {
        const parsed = JSON.parse(raw) as {
          code?: string;
          message?: string;
          title?: string;
        };
        if (parsed.code) code = parsed.code;
        if (parsed.message) message = parsed.message;
        if (parsed.title) title = parsed.title;
      } catch {
        message =
          `${res.status} ${res.statusText} — and the body was not JSON, which ` +
          `usually means something other than the gateway answered this request.`;
      }
      throw new ApiError(res.status, code, message, title);
    }

    // ⚠️ `as === "text"` is the CSV export; every other caller wants JSON.
    if (as === "text") return (await res.text()) as unknown as T;
    return (await res.json()) as T;
  }

  me(): Promise<StaffIdentity> {
    return this.get<StaffIdentity>("/admin/me");
  }

  customers(includeInternal: boolean): Promise<CustomerList> {
    return this.get<CustomerList>(
      `/admin/customers${includeInternal ? "?includeInternal=true" : ""}`,
    );
  }

  customer(id: string): Promise<CustomerDetail> {
    return this.get<CustomerDetail>(
      `/admin/customers/${encodeURIComponent(id)}`,
    );
  }

  /**
   * `from`/`to` are ISO instants. Omitted → the gateway's own 30-day default,
   * which it echoes back in `period` so the screen can name the window it is
   * showing rather than implying "all time".
   */
  aiCosts(id: string, from?: string, to?: string): Promise<AiCostReport> {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    const qs = q.toString();
    return this.get<AiCostReport>(
      `/admin/customers/${encodeURIComponent(id)}/ai-costs${qs ? `?${qs}` : ""}`,
    );
  }

  /**
   * Give an EXISTING company its own OpenAI project.
   *
   * ★ Omar's decision on OQ-116, 2026-09-13. A company that did not arrive by
   *   registration approval previously had no route to a project at all, and the
   *   documented "it happens on first question generation" fallback had never
   *   once succeeded — the request path runs under a role deliberately denied
   *   the table, so it always refused.
   *
   * ⚠️ `created: false` means an EXISTING project was ADOPTED, not that nothing
   *    happened. The project name is a pure function of the company, so the same
   *    company derives the same name in every environment — and ids were copied
   *    identically across ours. Adopting is right after a crash and joins two
   *    environments' spend when it happens across them, so the screen says which
   *    one occurred rather than just "done".
   */
  createOpenAiProject(id: string): Promise<{
    state: string;
    detail: string | null;
    created: boolean;
    createdServiceAccount: boolean;
  }> {
    return this.post(
      `/admin/customers/${encodeURIComponent(id)}/openai-project`,
      {},
    );
  }

  /**
   * The state of every provider's cost import — platform-wide, not per company.
   *
   * ★ Fetched ALONGSIDE a company's costs rather than folded into that response,
   *   because it answers a different question: the company route says what was
   *   imported, and this says whether importing is working at all. A category
   *   with no amount means "wait" or "act" depending only on this.
   */
  aiCostImportStatus(): Promise<CostImportStatus> {
    return this.get<CostImportStatus>("/admin/ai-costs/import-status");
  }

  /**
   * Provider charges that belong to no client.
   *
   * `start`/`end` are DATES (`YYYY-MM-DD`) and half-open, matching the way every
   * provider reports. Required by the route — it refuses to guess a window,
   * because a cost figure without its period is a number without a subject.
   */
  aiCostAttributionGaps(
    start: string,
    end: string,
  ): Promise<AiAttributionGaps> {
    const q = new URLSearchParams({ start, end });
    return this.get<AiAttributionGaps>(
      `/admin/ai-costs/attribution-gaps?${q.toString()}`,
    );
  }

  /**
   * ── THE IMAGE PROFILE ────────────────────────────────────────────────────
   *
   * Two scopes, matching Omar's decision 7: the photography instructions are
   * shared by every customer, and the illustration style belongs to one.
   *
   * ⚠️ `null` and "absent" are DIFFERENT in the save calls and the difference is
   * the whole of "restore defaults": `null` removes the override so the factory
   * text applies again; an absent key leaves that field untouched. The types
   * below keep them apart rather than collapsing both to optional
   * (`feedback_off_is_not_absent`).
   */
  imageProfile(): Promise<SharedImageProfile> {
    return this.get<SharedImageProfile>("/admin/image-profile");
  }

  saveImageProfile(body: {
    craft?: string | null;
    guidance?: string | null;
  }): Promise<SavedSharedImageProfile> {
    return this.put<SavedSharedImageProfile>("/admin/image-profile", body);
  }

  /**
   * ── THE COURSE-IMPORT RULES ──────────────────────────────────────────────
   *
   * One set for the whole platform, not one per customer: a single row stands
   * behind these, and the route is the only writer. Same `null`-versus-absent
   * rule as the image profile above, for the same reason.
   */
  courseImportRules(): Promise<CourseImportSettings> {
    return this.get<CourseImportSettings>("/admin/course-import");
  }

  saveCourseImportRules(
    body: CourseImportRuleWrites,
  ): Promise<SavedCourseImportRules> {
    return this.put<SavedCourseImportRules>("/admin/course-import", body);
  }

  /**
   * ── THE IMPORT FINDINGS RECORDER'S READER (migration 0026) ───────────────
   *
   * ★ Omar made this non-negotiable: *"Every field recorded must be reachable
   * through a reader, not only by direct SQL against the database. … A field
   * nobody can read is not recorded."* These three are that reader.
   */
  courseImportRuns(limit = 25): Promise<{ runs: ImportRunRow[] }> {
    return this.get<{ runs: ImportRunRow[] }>(
      `/admin/course-import/runs?limit=${encodeURIComponent(String(limit))}`,
    );
  }

  courseImportFindings(runId: string): Promise<{ runId: string; findings: ImportFindingRow[] }> {
    return this.get<{ runId: string; findings: ImportFindingRow[] }>(
      `/admin/course-import/runs/${encodeURIComponent(runId)}`,
    );
  }

  /**
   * The CSV as TEXT, fetched through the same authenticated client.
   *
   * ⚠️ NOT A PLAIN `<a href>`. The endpoint needs a bearer token, and a bare
   * link would answer 401 — the download has to go through the API client and
   * then become a blob. Same reason the export elsewhere in this Console does.
   */
  courseImportFindingsCsv(runId: string): Promise<string> {
    return this.getText(`/admin/course-import/runs/${encodeURIComponent(runId)}.csv`);
  }

  /**
   * ── THE EVIDENCE READERS (migration 0027) ────────────────────────────────
   *
   * ★ Omar, 2026-09-21: *"Provide the extraction, exact document input to the
   * planner, original AI architecture response before software changes, and the
   * adjusted architecture for the same identified run. If a historical raw
   * response was not retained, say so and capture it in the next verification
   * run. Do not present regenerated output as historical evidence."* And: *"The
   * findings CSV is not a substitute for an architecture export."*
   */
  courseImportFinal(runId: string): Promise<ImportFinalResponse> {
    return this.get<ImportFinalResponse>(
      `/admin/course-import/runs/${encodeURIComponent(runId)}/final`,
    );
  }

  courseImportArtifacts(runId: string): Promise<ImportArtifactsResponse> {
    return this.get<ImportArtifactsResponse>(
      `/admin/course-import/runs/${encodeURIComponent(runId)}/artifacts`,
    );
  }

  /**
   * One artefact's bytes, VERBATIM.
   *
   * ⚠️ Text, not JSON — `model_response_raw` is the model's reply exactly as it
   * arrived, and parsing it here would silently normalise the one thing it is
   * being kept for. Same reason the CSV goes through `getText`: the endpoint
   * needs a bearer token, so a plain `<a href>` would answer 401.
   */
  courseImportArtifact(runId: string, kind: string): Promise<string> {
    return this.getText(
      `/admin/course-import/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(kind)}`,
    );
  }

  workspaceMembers(id: string): Promise<WorkspaceMembers> {
    return this.get<WorkspaceMembers>(
      `/admin/customers/${encodeURIComponent(id)}/members`,
    );
  }

  illustrationStyle(id: string): Promise<CustomerIllustrationStyle> {
    return this.get<CustomerIllustrationStyle>(
      `/admin/customers/${encodeURIComponent(id)}/illustration-style`,
    );
  }

  saveIllustrationStyle(
    id: string,
    illustration: string | null,
  ): Promise<{
    saved: boolean;
    illustration: string;
    source: ImageProfileSource;
  }> {
    return this.put(
      `/admin/customers/${encodeURIComponent(id)}/illustration-style`,
      {
        illustration,
      },
    );
  }

  requests(): Promise<RequestList> {
    return this.get<RequestList>("/admin/requests");
  }

  approveRequest(id: string, input: ApproveInput): Promise<ApproveResult> {
    return this.post<ApproveResult>(
      `/admin/requests/${encodeURIComponent(id)}/approve`,
      input,
    );
  }

  /** `spam` files it away without a decision anybody will ever be told about. */
  rejectRequest(
    id: string,
    reason: string,
    spam: boolean,
  ): Promise<RejectResult> {
    return this.post<RejectResult>(
      `/admin/requests/${encodeURIComponent(id)}/reject`,
      {
        reason,
        spam,
      },
    );
  }

  suspendCustomer(id: string, reason: string): Promise<StatusChangeResult> {
    return this.post<StatusChangeResult>(
      `/admin/customers/${encodeURIComponent(id)}/suspend`,
      { reason },
    );
  }

  resumeCustomer(id: string, reason: string): Promise<StatusChangeResult> {
    return this.post<StatusChangeResult>(
      `/admin/customers/${encodeURIComponent(id)}/resume`,
      { reason },
    );
  }

  /**
   * Turn content protection on or off for this customer's FUTURE exports.
   * Packages already downloaded are unaffected — there is no reaching back
   * into a ZIP somebody already has.
   */
  setExportProtection(
    id: string,
    enabled: boolean,
    reason: string,
  ): Promise<ProtectionChangeResult> {
    return this.post<ProtectionChangeResult>(
      `/admin/customers/${encodeURIComponent(id)}/export-protection`,
      { enabled, reason },
    );
  }
}
